import React, { useEffect, useId, useState } from "react";
import { observer } from "mobx-react";
import { Button, ErrorText } from "~views/shared";
import addFile from "~assets/add-file-icon.svg";
import { IAttachment } from "netbank-shared/src/libs/models/Attachment/Attachment";
import styles from "./FileAttachment.scss";
import { useStores } from "netbank-shared/src/hooks";
import { tx } from "netbank-shared/src/libs/i18n";
import { bytesToSize } from "netbank-shared/src/libs/utils";
import { IInfoPopoverProps } from "../InfoPopover/InfoPopover";
import { LabelRow } from "../LabelRow/LabelRow";
import classNames from "classnames";
import { FileCard } from "./FileCard";

interface IFileAttachmentProps {
  attachments?: IAttachment[];
  addAttachments: (files: File[]) => Promise<void>;
  removeAttachment: (id: string) => Promise<void>;
  desktopLabel?: string;
  desktopLabelSuffix?: string;
  mobileLabel?: string;
  maxFileSizeLabel?: string;
  acceptedExtensionsLabel?: string;
  // Combination if mime types & extensions used for ease of mime validation and precision of extension validation
  acceptedMimeTypes?: string[];
  acceptedExtensions?: string[];
  multiple?: boolean;
  maxAttachments?: number;
  maxAttachmentSize?: number; // In bytes
  maxCombinedAttachmentSize?: number; // Combined size of all attached files at once. In bytes
  label?: string;
  infoPopover?: IInfoPopoverProps;
  disabled?: boolean;
  errors?: string[];
}

export const FileAttachment = observer(
  ({
    attachments = [],
    addAttachments,
    removeAttachment,
    desktopLabel,
    desktopLabelSuffix,
    mobileLabel,
    maxFileSizeLabel,
    acceptedExtensionsLabel,
    acceptedMimeTypes,
    acceptedExtensions,
    multiple,
    maxAttachments,
    maxAttachmentSize = 10 * 1024 * 1024, // Default to 10MB
    maxCombinedAttachmentSize = 20 * 1024 * 1024, // Default to 20MB
    label,
    infoPopover,
    disabled,
    errors,
  }: IFileAttachmentProps): JSX.Element => {
    const input: React.RefObject<HTMLInputElement> = React.useRef(null);
    const [uploading, setUploading] = useState<boolean>(false);
    const [dragFocus, setDragFocus] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string | undefined>(undefined);
    const { uiStore } = useStores();
    const inputId = useId();

    const [files, setFiles] = useState<(IAttachment & { status: string })[]>(
      attachments?.map((file) => ({ ...file, status: "done" })) || []
    );

    useEffect(() => {
      const hasErrors = (errors?.length || 0) > 0;
      setFiles((prev) => prev.map((file) => ({ ...file, status: hasErrors ? "error" : "done" })));
    }, [attachments, errors]);

    const onChange = async (fileList: FileList | null) => {
      if (disabled) return;

      const uploadedFiles = Array.from(fileList || []);
      if (uploadedFiles.length === 0) return;

      if (maxAttachments && uploadedFiles.length + files.length > maxAttachments) {
        setValidationError(tx("file.maxAttachmentsError", { count: maxAttachments }));
        return;
      }

      // Filter out files with duplicate names in current attachments, unallowed file size or unallowed mime types/file endings
      const filteredFiles = uploadedFiles.filter((f) => {
        const hasExistingFileName = files?.some((a) => a.file?.name === f.name);

        return (
          !hasExistingFileName &&
          f.size <= maxAttachmentSize &&
          (!acceptedMimeTypes ||
            acceptedMimeTypes?.find(
              (type) =>
                f.type === type &&
                (!acceptedExtensions || acceptedExtensions?.find((extension) => f.name.endsWith(extension)))
            ))
        );
      });

      // If lengths differ, show validation errors
      if (uploadedFiles.length !== filteredFiles.length) {
        if (acceptedMimeTypes && acceptedExtensions) {
          setValidationError(tx("file.attachmentValidationError", { extensions: acceptedExtensions }));
        } else {
          setValidationError(tx("file.duplicateAttachmentOrSizeError"));
        }

        return;
      }

      if (filteredFiles.length > 0) {
        const totalSize = filteredFiles.reduce((total, { size }) => total + size, 0);

        if (totalSize > maxCombinedAttachmentSize) {
          setValidationError(tx("file.maxCombinedFileSizeLabel", { limit: bytesToSize(maxCombinedAttachmentSize) }));
          return;
        }

        setUploading(true);
        setFiles((prev) => [
          ...prev,
          ...filteredFiles.map((file) => ({ file, id: file.name, name: file.name, status: "uploading" })),
        ]);

        await addAttachments(filteredFiles);

        setUploading(false);
      }
    };

    const handleRemoveAttachment = async (id: string) => {
      try {
        await removeAttachment(id);
        setFiles((prev) => prev.filter((file) => file.id !== id));
      } catch {
        //
      }
    };

    const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      if (disabled || uploading) return;

      e.preventDefault();
      setDragFocus(true);
      setValidationError(undefined);
    };

    const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      if (disabled || uploading) return;

      e.preventDefault();
      setDragFocus(false);
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
      if (disabled || uploading) return;

      e.preventDefault();
      setDragFocus(false);
      onChange(e.dataTransfer.files);
    };

    const handleButtonClick = () => {
      if (disabled) return;
      setValidationError(undefined);
      input.current?.click();
    };

    return (
      <div>
        {label && <LabelRow label={label} infoPopover={infoPopover} htmlFor={inputId} />}{" "}
        <input
          id={inputId}
          type="file"
          ref={input}
          style={{ display: "none" }}
          accept={(acceptedExtensions || acceptedMimeTypes)?.join(", ")}
          onChange={(e) => {
            e.preventDefault();
            onChange(e.target.files);
            e.target.value = "";
          }}
          multiple={multiple}
        />
        {!uiStore.isMobile && (
          <div
            className={classNames(styles.dropzone, { [styles.focus]: dragFocus })}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <Button
              type="text"
              iconPrefix={addFile}
              onClick={handleButtonClick}
              className={styles.button}
              title={desktopLabel || tx("file.desktopLabel")}
            />
            <span>{desktopLabelSuffix || tx("file.desktopLabelSuffix")}</span>
          </div>
        )}
        <div className={styles.attachmentRulesContainer}>
          {maxAttachmentSize && (
            <span>
              {maxFileSizeLabel || tx("file.maxFileSizeLabel")}: {bytesToSize(maxAttachmentSize)}.
            </span>
          )}
          {acceptedExtensions && (
            <span>
              {`${acceptedExtensionsLabel || tx("file.acceptedExtensionsLabel")}: ${tx("file.acceptedExtensions", {
                extensions: acceptedExtensions,
              })}`}
            </span>
          )}
        </div>
        {uiStore.isMobile && (
          <Button title={mobileLabel || tx("file.mobileLabel")} onClick={handleButtonClick} fullWidth />
        )}
        <div className={styles.attachmentsWrapper}>
          {files?.map((attachment) => (
            <FileCard
              key={attachment.id}
              attachment={attachment}
              removeAttachment={handleRemoveAttachment}
              disabled={disabled}
            />
          ))}
        </div>
        {validationError && <ErrorText error={validationError} />}
        {errors?.map((error) => (
          <ErrorText key={error} error={error} />
        ))}
      </div>
    );
  }
);
